// src/utils/exportUtils.js
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_COLOR = {
  Approved: [39, 174, 96],
  Rejected: [192, 57, 43],
  Reviewed: [41, 128, 185],
  Pending:  [243, 156, 18],
};

const LOGO_URL = process.env.PUBLIC_URL + '/gpp-logo.png';

// Load a (possibly remote) image and return a PNG data-URL + natural size.
// Resolves null if the image fails to load or is CORS-tainted, so the PDF
// can still be generated without the logo.
function loadImageDataUrl(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// ── EXCEL EXPORT ──────────────────────────────────────────
export function exportToExcel(rows, filename = 'EOI_Submissions') {
  const cols = [
    'Ref No','Submitted On','Code Name','Location','BHK','Price Range',
    'Unit Area (sqft)','Floor','Facing',
    'Applicant 1 Name','PAN','Aadhaar','Email','Mobile','Address','Profession','Company','Designation',
    'Applicant 2 Name','PAN 2','Email 2','Mobile 2',
    'Cheque No','Bank','Cheque Date',
    'Source Type','Source Name',
    'Submit Date','Submit Place','Submitted By','Verified By','Approved By',
    'Sourcing Manager','Sales Manager','Referral Partner',
    'Status',
  ];

  const data = rows.map(r => [
    r.ref_number,
    r.created_at ? r.created_at.split(' ')[0] : '',
    r.code_name, r.location, r.bhk_config, r.price_range,
    r.unit_area || '', r.pref_floor || '', r.facing || '',
    r.name1, r.pan1, r.aadhar1, r.email1, r.mobile1, r.address1, r.profession1, r.company1 || '', r.designation1 || '',
    r.name2 || '', r.pan2 || '', r.email2 || '', r.mobile2 || '',
    r.cheque_no, r.bank_name, r.cheque_date,
    r.source_type, r.source_name || '',
    r.submit_date, r.submit_place, r.submitted_by || '', r.verified_by || '', r.approved_by || '',
    r.sourcing_manager || '', r.sales_manager || '', r.referral_partner || '',
    r.status,
  ]);

  const ws = XLSX.utils.aoa_to_sheet([cols, ...data]);

  // Column widths
  ws['!cols'] = [
    {wch:14},{wch:12},{wch:18},{wch:18},{wch:8},{wch:18},
    {wch:14},{wch:12},{wch:10},
    {wch:22},{wch:12},{wch:16},{wch:26},{wch:13},{wch:35},{wch:24},{wch:22},{wch:18},
    {wch:22},{wch:12},{wch:26},{wch:13},
    {wch:14},{wch:18},{wch:12},
    {wch:14},{wch:18},
    {wch:12},{wch:16},{wch:18},{wch:18},{wch:18},
    {wch:18},{wch:18},{wch:20},
    {wch:10},
  ];

  // Header style
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (cell) {
      cell.s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: 'B8862E' } },
        alignment: { horizontal: 'center', wrapText: true },
        border: { bottom: { style: 'thin', color: { rgb: '888888' } } },
      };
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'EOI Submissions');

  // Summary sheet
  const summary = [
    ['Guru Punvaanii – EOI Export Summary'],
    ['Exported On', new Date().toLocaleString()],
    ['Total Records', rows.length],
    [''],
    ['Status', 'Count'],
  ];
  const statusCount = {};
  rows.forEach(r => { statusCount[r.status] = (statusCount[r.status] || 0) + 1; });
  Object.entries(statusCount).forEach(([s, c]) => summary.push([s, c]));

  const ws2 = XLSX.utils.aoa_to_sheet(summary);
  ws2['!cols'] = [{ wch: 22 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ── EXCEL IMPORT (bulk status update) ─────────────────────
// Reads an .xlsx/.xls/.csv file exported from this page and returns a list of
// { ref_number, status } pairs. The "Ref No" and "Status" columns are matched
// case-insensitively, so a file edited in Excel still parses correctly.
export function parseStatusImport(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!json.length) { resolve([]); return; }

        // Locate the Ref No / Status columns from the header keys.
        const keys = Object.keys(json[0]);
        const refKey = keys.find(k => /^ref\s*no$|ref[_\s]*number|reference/i.test(k.trim()));
        const statusKey = keys.find(k => /status/i.test(k.trim()));
        if (!refKey || !statusKey) {
          reject(new Error('File must contain "Ref No" and "Status" columns'));
          return;
        }

        const items = json
          .map(row => ({
            ref_number: String(row[refKey] ?? '').trim(),
            status: String(row[statusKey] ?? '').trim(),
          }))
          .filter(it => it.ref_number && it.status);
        resolve(items);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

// ── PDF EXPORT ────────────────────────────────────────────
export function exportToPDF(rows, filters = {}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });

  // Header band
  doc.setFillColor(184, 134, 46);
  doc.rect(0, 0, 420, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Guru Punvaanii – EOI Submissions', 14, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const dateStr = `Exported: ${new Date().toLocaleString()}   |   Records: ${rows.length}`;
  doc.text(dateStr, 420 - 14, 14, { align: 'right' });

  // Filter info
  let filterText = 'Filters: ';
  if (filters.from)   filterText += `From: ${filters.from}  `;
  if (filters.to)     filterText += `To: ${filters.to}  `;
  if (filters.status) filterText += `Status: ${filters.status}  `;
  if (filters.bhk)    filterText += `BHK: ${filters.bhk}  `;
  if (filterText === 'Filters: ') filterText += 'None (All records)';

  doc.setTextColor(80, 80, 80);
  doc.setFontSize(8);
  doc.text(filterText, 14, 28);

  // Table
  const head = [['Ref No','Date','Applicant','Mobile','Email','BHK','Price Range','Location','Source','Status']];
  const body = rows.map(r => [
    r.ref_number,
    r.created_at ? r.created_at.split(' ')[0] : '',
    r.name1,
    r.mobile1,
    r.email1,
    r.bhk_config,
    r.price_range,
    r.location,
    `${r.source_type}${r.source_name ? ' – ' + r.source_name : ''}`,
    r.status,
  ]);

  autoTable(doc, {
    startY: 32,
    head,
    body,
    styles: { fontSize: 7.5, cellPadding: 2.5, font: 'helvetica', overflow: 'linebreak' },
    headStyles: { fillColor: [26, 26, 26], textColor: 255, fontStyle: 'bold', halign: 'center' },
    alternateRowStyles: { fillColor: [253, 246, 227] },
    columnStyles: {
      0: { cellWidth: 22, halign: 'center' },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 38 },
      3: { cellWidth: 24 },
      4: { cellWidth: 48 },
      5: { cellWidth: 16, halign: 'center' },
      6: { cellWidth: 32 },
      7: { cellWidth: 38 },
      8: { cellWidth: 36 },
      9: { cellWidth: 20, halign: 'center' },
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 9) {
        const status = data.cell.raw;
        const color = STATUS_COLOR[status] || [120, 120, 120];
        doc.setFillColor(...color);
        doc.roundedRect(data.cell.x + 1, data.cell.y + 1.5, data.cell.width - 2, data.cell.height - 3, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 0.5, { align: 'center', baseline: 'middle' });
        return false;
      }
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 9) {
        data.cell.text = '';
      }
    },
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(160, 160, 160);
    doc.text('Guru Punvaanii | Fair Policy | Fair Property', 14, doc.internal.pageSize.height - 6);
    doc.text(`Page ${i} of ${pageCount}`, 420 - 14, doc.internal.pageSize.height - 6, { align: 'right' });
  }

  doc.save(`EOI_Submissions_${new Date().toISOString().split('T')[0]}.pdf`);
}

// ── SINGLE-RECORD PDF EXPORT ──────────────────────────────
// Generates a one-page A4 PDF for a single EOI submission, with the
// Guru Punvaanii logo at the top. Async because the logo loads remotely.
export async function exportSingleToPDF(r) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 14;

  // Logo (centered) — falls back to brand text if it can't load.
  const logo = await loadImageDataUrl(LOGO_URL);
  if (logo) {
    const logoW = 46;
    const logoH = logoW * (logo.h / logo.w);
    doc.addImage(logo.dataUrl, 'PNG', (pageW - logoW) / 2, y, logoW, logoH);
    y += logoH + 3;
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(184, 134, 46);
    doc.text('Guru Punvaanii', pageW / 2, y + 8, { align: 'center' });
    y += 14;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('Fair Policy | Fair Property', pageW / 2, y, { align: 'center' });
  y += 5;

  doc.setDrawColor(184, 134, 46);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // Title + ref number (left) and status pill (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  doc.text('Expression of Interest', margin, y);

  const status = r.status || 'Pending';
  const sColor = STATUS_COLOR[status] || [120, 120, 120];
  doc.setFontSize(9);
  const sw = doc.getTextWidth(status) + 8;
  doc.setFillColor(...sColor);
  doc.roundedRect(pageW - margin - sw, y - 5, sw, 7, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(status, pageW - margin - sw / 2, y, { align: 'center' });

  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  const submitted = r.created_at ? r.created_at.split(' ')[0] : '';
  doc.text(`Ref No: ${r.ref_number || '—'}     Submitted: ${submitted}`, margin, y);
  y += 6;

  const v = (x) => (x === null || x === undefined || x === '' ? '—' : String(x));

  const section = (title, body) => {
    autoTable(doc, {
      startY: y,
      head: [[{ content: title, colSpan: 2 }]],
      body,
      theme: 'grid',
      margin: { left: margin, right: margin },
      headStyles: { fillColor: [26, 26, 26], textColor: 255, fontStyle: 'bold', fontSize: 9.5, cellPadding: 2 },
      styles: { fontSize: 9, cellPadding: 2, font: 'helvetica', overflow: 'linebreak', lineColor: [225, 225, 225] },
      columnStyles: {
        0: { cellWidth: 48, fontStyle: 'bold', textColor: [90, 90, 90], fillColor: [250, 247, 240] },
        1: { cellWidth: pageW - margin * 2 - 48 },
      },
    });
    y = doc.lastAutoTable.finalY + 4;
  };

  section('Project Details', [
    ['Code Name', v(r.code_name)],
    ['Location', v(r.location)],
    ['BHK Config', v(r.bhk_config)],
    ['Price Range', v(r.price_range)],
    ['Unit Area', r.unit_area ? `${r.unit_area} sq.ft.` : '—'],
    ['Pref. Floor', v(r.pref_floor)],
    ['Facing', v(r.facing)],
    ['Cheque No.', v(r.cheque_no)],
    ['Bank Name', v(r.bank_name)],
    ['Cheque Date', v(r.cheque_date)],
  ]);

  section('Applicant 1 (Primary)', [
    ['Full Name', v(r.name1)],
    ['PAN', v(r.pan1)],
    ['Aadhaar', v(r.aadhar1)],
    ['Email', v(r.email1)],
    ['Mobile', v(r.mobile1)],
    ['Profession', v(r.profession1)],
    ['Company', v(r.company1)],
    ['Designation', v(r.designation1)],
    ['Address', v(r.address1)],
  ]);

  if (r.name2) {
    section('Applicant 2 (Co-applicant)', [
      ['Full Name', v(r.name2)],
      ['PAN', v(r.pan2)],
      ['Aadhaar', v(r.aadhar2)],
      ['Email', v(r.email2)],
      ['Mobile', v(r.mobile2)],
      ['Profession', v(r.profession2)],
      ['Company', v(r.company2)],
      ['Designation', v(r.designation2)],
      ['Address', v(r.address2)],
    ]);
  }

  section('Source & Officials', [
    ['Source Type', v(r.source_type)],
    ['Source Name', v(r.source_name)],
    ['Submit Date', v(r.submit_date)],
    ['Submit Place', v(r.submit_place)],
    ['Submitted By', v(r.submitted_by)],
    ['Verified By', v(r.verified_by)],
    ['Approved By', v(r.approved_by)],
    ['Sourcing Mgr', v(r.sourcing_manager)],
    ['Sales Mgr', v(r.sales_manager)],
    ['Referral Partner', v(r.referral_partner)],
  ]);

  // Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(160, 160, 160);
    doc.text('Guru Punvaanii | Fair Policy | Fair Property', margin, doc.internal.pageSize.height - 6);
    doc.text(`Page ${i} of ${pageCount}`, pageW - margin, doc.internal.pageSize.height - 6, { align: 'right' });
  }

  doc.save(`EOI_${r.ref_number || 'submission'}.pdf`);
}
